import { NavigationGuard } from 'vue-router';

import store from '@/store';
import auth from '@/services/AuthService';
import authService from '@/services/AuthService';
import { authRoutes } from '@/constants/RouterConsts';
import {
  captureAppContext,
  clearAppContext,
  guidesRedirectAction,
  guidesReturnTo,
  isGuidesContext,
  isGuidesOnlyCompany,
  pickGuidesCompany,
} from '@/helpers/appContext';
import { getProductsUrl } from '@/helpers/productsUrl';
import {
  HOME,
  LOGIN,
  SIGNUP,
  CHECKOUT,
  BROWSE_SHARED,
  PASSWORD_RESET,
  SIGNUP_INVITED,
  LOGIN_SOCIAL_FAIL,
  PASSWORD_RESET_TOKEN,
  ACCOUNT_RECOVERY_REDEEM,
  ACCOUNT_RECOVERY_CANCEL,
  LOGIN_SOCIAL_CALLBACK,
  LOGIN_SOCIAL_TWO_FACTOR,
  BROWSE,
  BROWSE_PARAGRAPH,
  BROWSE_TEXT_TAB,
  BILLING_PLANS,
} from '@/routerNames';

const publicRoutes = [
  LOGIN,
  SIGNUP,
  CHECKOUT,
  PASSWORD_RESET,
  SIGNUP_INVITED,
  LOGIN_SOCIAL_FAIL,
  PASSWORD_RESET_TOKEN,
  ACCOUNT_RECOVERY_REDEEM,
  ACCOUNT_RECOVERY_CANCEL,
  LOGIN_SOCIAL_CALLBACK,
  LOGIN_SOCIAL_TWO_FACTOR,
  BROWSE_SHARED,
  BROWSE,
  BROWSE_PARAGRAPH,
  BROWSE_TEXT_TAB,
  'BitbucketOAuthCallback',
  'ConfluenceOAuthCallback',
];

// Open to anonymous visitors, but a signed-in user sees more on them, so they still ask about the session.
// The checkout confirms a payment on the signed-in user's company, and the billing host has no other way to load it.
const sessionAwarePublicRoutes = [BROWSE_SHARED, BROWSE, BROWSE_PARAGRAPH, BROWSE_TEXT_TAB, CHECKOUT];

const AuthMiddleware: NavigationGuard = async (to, from, next) => {
  if (authRoutes.indexOf(to.name) && to.params.token) {
    next();
    return;
  }

  // SNAP-2309: the session is an HttpOnly cookie, so the client cannot tell whether one exists. Ask the
  // API, and only on routes whose answer matters, so anonymous visitors to most public pages do not probe.
  const needsSessionCheck =
    !publicRoutes.includes(to.name) || authRoutes.indexOf(to.name) >= 0 || sessionAwarePublicRoutes.includes(to.name);
  const hasSession = auth.user ? true : needsSessionCheck ? await authService.probeSession() : false;

  if ((!store.state.company || !auth.user) && !publicRoutes.includes(to.name)) {
    store.commit('setCompanyLoading', false);
  }

  // `session=1` means a flow just set the cookie and wants LoginForm to finish it (social login,
  // cross-company redirect). Do not bounce it to HOME first.
  if (hasSession && authRoutes.indexOf(to.name) >= 0 && to.name !== LOGIN_SOCIAL_CALLBACK && to.query.session !== '1') {
    // An already-authenticated visit to /login|/signup never mounts LoginForm,
    // so the guides context must be handled here: a paid AND howto-entitled
    // company → straight to the Guides app (C2); a company paid for something
    // else (e.g. Snaplore/kb) → upsell to the Guides plans page instead of
    // free access (D2); unpaid → capture the context so the plans page shows
    // Guides prices after the HOME → billing redirect chain. A plain visit
    // (no app/redirect param) resets stale context, same as LoginForm.
    captureAppContext(to.query);
    // SNAP-2231: a Guides-only company is in Guides mode with or without the param.
    if (isGuidesContext(to.query) || isGuidesOnlyCompany(store.state.company)) {
      const action = guidesRedirectAction(store.state.company);
      if (action === 'products') {
        const returnTo = guidesReturnTo(to.query);
        clearAppContext();
        window.location.href = getProductsUrl(returnTo);
        return;
      }
      if (action === 'plans-upsell') {
        // Not this company, but another one of this user may have Guides: LoginForm picks it and switches to it.
        const guidesCompany = pickGuidesCompany(store.state.userCompanies, store.state.user?.id);
        if (guidesCompany?.productAccess?.howto === true && guidesCompany.id !== store.state.company?.id) {
          next({ name: LOGIN, query: { ...to.query, session: '1' } });
          return;
        }
        next({ name: BILLING_PLANS, query: { app: 'guides' } });
        return;
      }
    }
    next({ name: HOME });
    return;
  }

  if (publicRoutes.indexOf(to.name) >= 0) {
    next();
  } else {
    if (hasSession) {
      next();
    } else {
      if (to.fullPath === '/') {
        next({ name: LOGIN });
        return;
      }
      next({ name: LOGIN, query: { redirect: to.fullPath } });
    }
  }
};

export default AuthMiddleware;
